import { formatDue, isForeignZone, msUntilDue, relativeDue } from '@/lib/task-time';
import type { TaskDue } from '@/lib/types';

/*
 * The due label on a compact task row: "Wed, 14 Oct 2026, 15:30", and when
 * open and overdue "Wed, 14 Oct 2026, 15:30 · 30d late". Owner's request,
 * 2026-10-12: the full date with its weekday, never a bare "Wed" or a bare
 * "30d late". SUPERSEDED: "2d late", "Today 3:00 pm", "Tomorrow", "Fri",
 * "12 Oct". The list's own grouping already says Today / Upcoming, so the
 * label does not repeat it.
 *
 * Rendered on the ASSIGNEE's calendar (`due.timeZone`), the same clock
 * `formatDue` renders. Built from `due.utc` with a time-zoned formatter, never
 * from `new Date('YYYY-MM-DD')`. Lateness is measured on `due.utc` against the
 * clock the caller hands in, which on /tasks is the skew-corrected one from
 * the brief.
 *
 * The full rendering (`formatDue`, which names a foreign zone) rides on
 * `title`, so the compact label never has to carry the zone itself.
 *
 * "late" is a WORD, never colour alone: the chip is also toned, but the text
 * says it.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const timeFormatters = new Map<string, Intl.DateTimeFormat | null>();
const longDateFormatters = new Map<string, Intl.DateTimeFormat | null>();

function cached(
  cache: Map<string, Intl.DateTimeFormat | null>,
  key: string,
  build: () => Intl.DateTimeFormat,
): Intl.DateTimeFormat | null {
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  let made: Intl.DateTimeFormat | null;
  try {
    made = build();
  } catch {
    made = null;
  }
  cache.set(key, made);
  return made;
}

function format(formatter: Intl.DateTimeFormat | null, at: number): string | null {
  if (!formatter) return null;
  try {
    return formatter.format(at);
  } catch {
    return null;
  }
}

export interface DueChip {
  /** The whole label as one string: date, time, and "· Nd late" when overdue. */
  text: string;
  /** 'Wed, 14 Oct 2026' on the assignee's calendar (or `formatDue`'s fallback). */
  date: string;
  /** '15:30' on the assignee's clock; null when `date` is the fallback. */
  time: string | null;
  /** '30d late' when open and past due, else null. */
  lateText: string | null;
  /** Open and past due — the chip is toned danger AND says "late". */
  late: boolean;
  /** The full rendering for `title`: date, time, zone when foreign, relative. */
  title: string;
}

/**
 * `null` for an undated task: an undated task is ordinary, so the row says
 * nothing about time at all.
 */
export function dueChip(
  due: TaskDue | null,
  options: { open: boolean; viewerTimeZone: string; now?: Date; assigneeName?: string | null },
): DueChip | null {
  if (!due) return null;
  const diff = msUntilDue(due, options.now);
  const at = Date.parse(due.utc);
  const zone = typeof due.timeZone === 'string' ? due.timeZone.trim() : '';

  const full = formatDue(due, options.viewerTimeZone);
  const relative = relativeDue(due, options.now);
  const foreign = isForeignZone(due, options.viewerTimeZone);
  const title = [
    `Due ${full}`,
    relative ? `(${relative})` : null,
    foreign ? `on ${options.assigneeName?.trim() || 'the assignee'}'s clock` : null,
  ]
    .filter(Boolean)
    .join(' ');

  if (diff === null || Number.isNaN(at)) {
    return { text: full, date: full, time: null, lateText: null, late: false, title };
  }

  // The assignee's calendar, as `formatDue` renders it; the viewer's only when
  // the due carries no zone at all.
  const calendarZone = zone || options.viewerTimeZone;
  const date = dateWithWeekday(calendarZone, at) ?? full;
  // Every `TaskDue` carries a time (`toDueAtString` refuses a blank one), so the
  // time is always shown. When the date fell back to `full`, that already
  // carries the time and is not repeated.
  const time = date === full ? null : timeOfDay(calendarZone, at);

  const late = options.open && diff < 0;
  let lateText: string | null = null;
  if (late) {
    const magnitude = -diff;
    lateText =
      magnitude < HOUR_MS
        ? `${Math.max(1, Math.floor(magnitude / MINUTE_MS))}m late`
        : magnitude < DAY_MS
          ? `${Math.floor(magnitude / HOUR_MS)}h late`
          : `${Math.floor(magnitude / DAY_MS)}d late`;
  }

  const text = [time ? `${date}, ${time}` : date, lateText].filter(Boolean).join(' · ');
  return { text, date, time, lateText, late, title };
}

/**
 * 'Wed, 14 Oct 2026' on a zone's calendar, assembled from parts so the shape
 * does not depend on the locale's own punctuation. Null when unformattable.
 */
function dateWithWeekday(timeZone: string, at: number): string | null {
  // en-US for the PARTS only (the order is assembled below): en-GB's ICU spells
  // September 'Sept', where every other month is three letters.
  const formatter = cached(longDateFormatters, timeZone, () =>
    new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone,
    }),
  );
  if (!formatter) return null;
  try {
    const parts = formatter.formatToParts(at);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? '';
    const weekday = part('weekday');
    const day = part('day');
    const month = part('month');
    const year = part('year');
    if (!weekday || !day || !month || !year) return null;
    return `${weekday}, ${day} ${month} ${year}`;
  } catch {
    return null;
  }
}

/** '15:30' on a zone's clock. Null when unformattable. */
function timeOfDay(timeZone: string, at: number): string | null {
  return format(
    cached(timeFormatters, timeZone, () =>
      new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone,
      }),
    ),
    at,
  );
}
